import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  
  OutlinedInput,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { post } from '../../utils/fetch';
import { ENV, SNACKBAR_DURATION } from '../../constants';
import validateNumber from '../../utils/validateNumber';
import { formatPhoneForDisplay, normalizePhoneDigits } from '../../utils/phoneInput';
import { useCurrentUser } from '../../context/UserContext';
import { authenticatedPost } from '../../utils/authenticatedFetch';

const styles = {
  main: {
    backgroundColor: '#fff',
    padding: '18px 22px',
  },
  phoneInput: {
    margin: '20px 0',
    width: '100%',
  },
  codeInput: {
    margin: '20px 0',
    width: '130px',
  },
  continueBtn: {
    width: '100%',
    marginBottom: '10px'
  }
}

export default function SignupPhoneNumber() {
  
  const [requested, setRequested] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'success',
  });

  return (
    <Box sx={styles.main}>
      <Typography variant="h2" color="primary">
        Verify your Phone Number
      </Typography>
      {requested ? (
        <CodeEntry setSnackbar={setSnackbar} />
      ) : (
        <PhoneNumberEntry
          setRequested={setRequested}
          setSnackbar={setSnackbar}
        />
      )}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={SNACKBAR_DURATION}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.type}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

SignupPhoneNumber.propTypes = {
  onNext: PropTypes.func,
};

function PhoneNumberEntry({ setRequested, setSnackbar }) {
  
  const theme = useTheme();
  const [number, setNumber] = useState('');

  function onNumberChange(e) {
    setNumber(normalizePhoneDigits(e.target.value));
  }

  const { refetch } = useQuery({
    queryKey: ['sendLoginRequest'],
    queryFn: async () => {
      const response = await post(`${ENV.API_URL}/sendLoginUrl`, {
        phoneNumber: number,
        withUrl: false,
      });

      if (!response.ok) {
        throw new Error('Failed to send verification code');
      }

      return response.json();
    },
    enabled: false,
  });

  const handleRequestCode = async () => {
    const result = await refetch();

    if (result.error) {
      setSnackbar({
        open: true,
        message: result.error?.message || 'Something happened...',
        type: 'error',
      });
      return;
    }

    if (!result.data?.success) {
      setSnackbar({
        open: true,
        message: 'Failed to send verification code',
        type: 'error',
      });
      return;
    }

    setRequested(true);
  };

  return (
    <Box>
      <Typography
        variant="body2"
        style={{ marginTop: '10px', color: theme.palette.gray.darker }}
      >
        To sign up, we need to verify your phone number by texting you a code.
      </Typography>
      <FormControl sx={styles.phoneInput} variant="outlined">
        <InputLabel htmlFor="number">Phone Number</InputLabel>
        <OutlinedInput
          id="number"
          value={formatPhoneForDisplay(number)}
          onChange={onNumberChange}
          label="Phone Number"
          required
          inputProps={{
            inputMode: 'tel',
            autoComplete: 'tel-national',
            'data-testid': 'signup-phone-input',
          }}
        />
      </FormControl>
      <Button
        onClick={handleRequestCode}
        color="primary"
        variant="contained"
        disabled={!validateNumber(number)}
        style={{ marginBottom: '10px' }}
        disableElevation
        fullWidth
        data-testid="signup-phone-next-button"
      >
        Next
      </Button>
      <Box display="flex" justifyContent="center">
        <Typography
          variant="body2"
          style={{ color: theme.palette.gray.darker }}
        >
          Standard message & data rates may apply.
        </Typography>
      </Box>
    </Box>
  );
}

PhoneNumberEntry.propTypes = {
  setRequested: PropTypes.func,
  setSnackbar: PropTypes.func,
};

function isValidCode(code) {
  return code?.length === 8 && !Number.isNaN(Number(code));
}

function CodeEntry({ setSnackbar }) {
  
  const theme = useTheme();
  const [code, setCode] = useState('');
  const { loadUser } = useCurrentUser();

  const { refetch } = useQuery({
    queryKey: ['verifyLogin', code],
    queryFn: async () => {
      const response = await post(`${ENV.API_URL}/urls/code/${code}`);
      if (!response.ok) {
        throw new Error('Invalid verification code');
      }
      return response.json();
    },
    enabled: false,
  });

  const handleContinue = async () => {
    const verifyResult = await refetch();
    if (verifyResult.error) {
      setSnackbar({
        open: true,
        message: verifyResult.error?.message || 'Something happened...',
        type: 'error',
      });
      return;
    }

    const token = verifyResult.data?.data?.token;
    if (!token) {
      setSnackbar({
        open: true,
        message: 'Invalid verification code',
        type: 'error',
      });
      return;
    }

    const response = await authenticatedPost(`${ENV.API_URL}/login`, {
      token,
    });

    if (!response.ok) {
      setSnackbar({
        open: true,
        message: 'Login failed',
        type: 'error',
      });
      return;
    }

    await loadUser();
  };

  return (
    <Box>
      <Typography
        variant="body2"
        style={{ marginTop: '10px', color: theme.palette.gray.darker }}
      >
        You should get a text with a 8-digit code. Enter your code here:
      </Typography>
      <Box display="flex" alignItems="center" justifyContent="center">
        <FormControl sx={styles.codeInput} variant="outlined">
          <InputLabel htmlFor="code">Code</InputLabel>
          <OutlinedInput
            id="code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            label="Code"
            type="text"
            inputProps={{
              inputMode: 'numeric',
              autoComplete: 'one-time-code',
              pattern: '\\d{8}',
              'data-testid': 'signup-code-input',
            }}
            required
          />
        </FormControl>
      </Box>
      {isValidCode(code) && (
        <Button
          onClick={handleContinue}
          color="primary"
          variant="contained"
          style={styles.continueBtn}
          disableElevation
          data-testid="signup-code-continue-button"
        >
          Continue to Sign Up
        </Button>
      )}
    </Box>
  );
}

CodeEntry.propTypes = {
  setSnackbar: PropTypes.func,
};
